import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Stethoscope, Heart, ArrowLeft, CheckCircle } from 'lucide-react';
import { FEATURE_KEYS, isFeatureEnabled } from '../../redux/featureSlice';

export default function RegisterChoice() {
  const navigate = useNavigate();
  const { t } = useTranslation('register');
  const doctorRegEnabled = useSelector((s) => isFeatureEnabled(s, FEATURE_KEYS.authDoctorRegistration));
  const patientRegEnabled = useSelector((s) => isFeatureEnabled(s, FEATURE_KEYS.authPatientRegistration));
  const featureLoaded = useSelector((s) => s.feature?.loaded);

  // If admin has disabled both registration flows, there's nothing to choose
  // — bounce back to the home page rather than rendering an empty layout.
  useEffect(() => {
    if (featureLoaded && !doctorRegEnabled && !patientRegEnabled) {
      navigate('/', { replace: true });
    }
  }, [featureLoaded, doctorRegEnabled, patientRegEnabled, navigate]);

  return (
    <div className="min-h-screen w-full bg-white overflow-hidden py-8 sm:py-12" data-testid="register-choice-container">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#4ABDBD]/5 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#4ABDBD]/5 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-blob animation-delay-2000"></div>
      </div>

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animate-slideInUp { animation: slideInUp 0.8s ease-out; }
        .animate-float { animation: float 6s ease-in-out infinite; }
      `}</style>

      {/* Main Container */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6" data-testid="register-choice-main-container">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-6 left-6 sm:left-8 flex items-center gap-2 text-gray-700 hover:text-[#4ABDBD] transition-colors font-medium text-sm sm:text-base"
          data-testid="register-choice-back-home-button"
        >
          <ArrowLeft className="w-5 h-5" data-testid="register-choice-back-home-icon" />
          {t('register.back_home')}
        </button>

        {/* Header Section */}
        <div className="text-center mb-12 sm:mb-16 animate-slideInUp" data-testid="register-choice-header-section">
          <h1 className="gradient-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-testid="register-choice-heading-text">
            {t('register.join_us')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto" data-testid="register-choice-subheading-text">
            {t('register.choose_role')}
          </p>
        </div>

        {/* Registration Cards */}
        <div
          className={`grid grid-cols-1 ${doctorRegEnabled && patientRegEnabled ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md'} gap-6 sm:gap-8 max-w-6xl w-full px-4 sm:px-0`}
          data-testid="register-choice-cards-section"
        >
          {/* Doctor Registration Card */}
          {doctorRegEnabled && (
          <div
            className="bg-white border-2 border-gray-200 rounded-2xl p-6 sm:p-8 hover:border-[#4ABDBD] hover:shadow-xl transition-all duration-300 animate-slideInUp group flex flex-col h-full"
            style={{ animationDelay: '0.1s' }}
            data-testid="register-choice-doctor-card"
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Stethoscope className="text-white w-10 h-10 sm:w-12 sm:h-12" data-testid="register-choice-doctor-icon" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col">
              {/* Title */}
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 text-center" data-testid="register-choice-doctor-title">
                {t('register.doctor_title')}
              </h2>

              {/* Description */}
              <p className="text-gray-600 text-sm sm:text-base mb-6 text-center" data-testid="register-choice-doctor-description">
                {t('register.doctor_description')}
              </p>

              {/* Features */}
              <div className="space-y-3 mb-6 flex-1" data-testid="register-choice-doctor-features">
                {[
                  t('register.doctor_feature1'),
                  t('register.doctor_feature2'),
                  t('register.doctor_feature3'),
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3" data-testid={`register-choice-doctor-feature-${index}`}>
                    <CheckCircle className="w-5 h-5 text-[#4ABDBD] shrink-0 mt-0.5" data-testid={`register-choice-doctor-feature-icon-${index}`} />
                    <span className="text-gray-700 text-sm sm:text-base" data-testid={`register-choice-doctor-feature-text-${index}`}>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Button at bottom */}
              <button
                onClick={() => navigate('/doctor-register')}
                className="w-full py-3 px-6 border-2 border-[#4ABDBD] text-[#4ABDBD] font-semibold rounded-lg hover:bg-[#4ABDBD] hover:text-white transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer mt-auto"
                data-testid="register-choice-doctor-register-button"
              >
                <Stethoscope className="w-5 h-5" data-testid="register-choice-doctor-button-icon" />
                {t('register.register_as_doctor')}
              </button>
            </div>
          </div>
          )}

          {/* Patient Registration Card */}
          {patientRegEnabled && (
          <div
            className="bg-white border-2 border-gray-200 rounded-2xl p-6 sm:p-8 hover:border-[#4ABDBD] hover:shadow-xl transition-all duration-300 animate-slideInUp group flex flex-col h-full"
            style={{ animationDelay: '0.2s' }}
            data-testid="register-choice-patient-card"
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Heart className="text-white w-10 h-10 sm:w-12 sm:h-12" data-testid="register-choice-patient-icon" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col">
              {/* Title */}
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 text-center" data-testid="register-choice-patient-title">
                {t('register.patient_title')}
              </h2>

              {/* Description */}
              <p className="text-gray-600 text-sm sm:text-base mb-6 text-center" data-testid="register-choice-patient-description">
                {t('register.patient_description')}
              </p>

              {/* Features */}
              <div className="space-y-3 mb-6 flex-1" data-testid="register-choice-patient-features">
                {[
                  t('register.patient_feature1'),
                  t('register.patient_feature2'),
                  t('register.patient_feature3'),
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3" data-testid={`register-choice-patient-feature-${index}`}>
                    <CheckCircle className="w-5 h-5 text-[#4ABDBD] shrink-0 mt-0.5" data-testid={`register-choice-patient-feature-icon-${index}`} />
                    <span className="text-gray-700 text-sm sm:text-base" data-testid={`register-choice-patient-feature-text-${index}`}>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Button at bottom */}
              <button
                onClick={() => navigate('/patient-register')}
                className="w-full py-3 px-6 border-2 border-[#4ABDBD] text-[#4ABDBD] font-semibold rounded-lg hover:bg-[#4ABDBD] hover:text-white transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer mt-auto"
                data-testid="register-choice-patient-register-button"
              >
                <Heart className="w-5 h-5" data-testid="register-choice-patient-button-icon" />
                {t('register.register_as_patient')}
              </button>
            </div>
          </div>
          )}
        </div>

        {/* Already have account */}
        <div className="text-center mt-12 sm:mt-16 animate-slideInUp" style={{ animationDelay: '0.3s' }} data-testid="register-choice-login-section">
          <p className="text-gray-600 text-base sm:text-lg" data-testid="register-choice-login-text">
            {t('register.already_have_account')}{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-[#4ABDBD] font-bold hover:text-[#3A9B9B] transition-colors underline"
              data-testid="register-choice-login-button"
            >
              {t('register.login_here')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

